import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { CheckCircle, Error, HourglassEmpty } from "@material-ui/icons";
import { useGetWorkflowRunsByOwnerQuery, WorkflowStatus } from "@graphql";
import { useGlobalStore } from "@hooks";
import {
  BOOKING_SLIDER,
  KIT_SLIDER,
  CONSULTANT_SLIDER,
  BookingProfileLocation,
  KitProfileLocation,
  ConsultantProfileLocation
} from "@navigation";
import PulsingDot from "../common/pulsing-dot";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    height: "100vh",
    overflow: "hidden",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2)
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center"
  },
  filterSelect: {
    minWidth: 160
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  stepsCell: {
    maxWidth: 220
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  stepDone: {
    color: "#4caf50"
  },
  stepFailed: {
    color: theme.palette.error.main
  },
  entityLink: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    "&:hover": { textDecoration: "underline" }
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 12
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: theme.palette.text.secondary
  }
}));

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  approveBookingKit: "Godkänn bokningskit",
  createBookingKit: "Skapa bokningskit",
  notifyBaOnServicePrefExp: "Notifiera BA om utgående tjänstepreferens",
  pollWorkrequestMailbox: "Hämta arbetsförfrågningar",
  processIncomingWorkrequest: "Behandla inkommande arbetsförfrågan",
  processMedloWorkrequest: "Behandla Medlo-arbetsförfrågan",
  processSubmittedTimereport: "Behandla inlämnad tidrapport",
  publishSelfInvoicingContract: "Publicera egenanställningskontrakt",
  publishSubcontractorInvoice: "Publicera underleverantörsfaktura",
  publishWccConsultantContract: "Publicera WCC-konsultkontrakt",
  reactivatePausedQualityItem: "Återaktivera pausad kvalitetskontroll",
  scanExpiringServicePref: "Sök utgående tjänstepreferenser",
  publishPayFormRecordsToFortnox: "Publicera löneunderlag till Fortnox",
  publishSickPayFormToFortnox: "Publicera sjuklöneunderlag till Fortnox",
  sendTimereportApprovedNotification: "Skicka notifiering om godkänd tidrapport",
  sendTimereportToCustomer: "Skicka tidrapport till kund"
};

const STATUS_OPTIONS = [
  { value: "", label: "Alla statusar" },
  { value: WorkflowStatus.Running, label: "Pågår" },
  { value: WorkflowStatus.Completed, label: "Slutförd" },
  { value: WorkflowStatus.Failed, label: "Misslyckades" },
  { value: WorkflowStatus.Canceled, label: "Avbruten" },
  { value: WorkflowStatus.Terminated, label: "Terminerad" }
];

const StatusBadge = ({ status }: { status: WorkflowStatus }) => {
  const classes = useStyles();
  if (status === WorkflowStatus.Running) {
    return (
      <span className={classes.statusCell}>
        <PulsingDot variant="info" size={10} />
        <Typography variant="body2" style={{ color: "#1976d2" }}>
          Pågår...
        </Typography>
      </span>
    );
  }
  if (status === WorkflowStatus.Completed) {
    return (
      <span className={classes.statusCell}>
        <CheckCircle style={{ fontSize: 16, color: "#4caf50" }} />
        <Typography variant="body2" style={{ color: "#4caf50" }}>
          Slutförd
        </Typography>
      </span>
    );
  }
  if (status === WorkflowStatus.Canceled) {
    return (
      <span className={classes.statusCell}>
        <Error style={{ fontSize: 16, color: "#9e9e9e" }} />
        <Typography variant="body2" style={{ color: "#9e9e9e" }}>
          Avbruten
        </Typography>
      </span>
    );
  }
  if (status === WorkflowStatus.Terminated) {
    return (
      <span className={classes.statusCell}>
        <Error style={{ fontSize: 16, color: "#ff6d00" }} />
        <Typography variant="body2" style={{ color: "#ff6d00" }}>
          Terminerad
        </Typography>
      </span>
    );
  }
  return (
    <span className={classes.statusCell}>
      <Error style={{ fontSize: 16, color: "#f44336" }} />
      <Typography variant="body2" style={{ color: "#f44336" }}>
        Misslyckades
      </Typography>
    </span>
  );
};

type Step = {
  _id: string;
  title: string;
  startedAt?: Date | null;
  completedAt?: Date | null;
  failedAt?: Date | null;
};

const StepsCell = ({ steps }: { steps: Step[] }) => {
  const classes = useStyles();
  if (!steps.length) return <Typography variant="body2">—</Typography>;

  const sorted = [...steps].sort((a, b) => {
    const aTime = a.startedAt ?? a.completedAt ?? a.failedAt;
    const bTime = b.startedAt ?? b.completedAt ?? b.failedAt;
    if (!aTime && !bTime) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    return new Date(aTime).getTime() - new Date(bTime).getTime();
  });

  const done = sorted.filter((s) => s.completedAt).length;
  const current =
    sorted.find((s) => !s.completedAt && !s.failedAt) ?? sorted[sorted.length - 1];

  return (
    <Tooltip
      title={
        <div style={{ padding: "4px 0", minWidth: 260 }}>
          {sorted.map((s) => {
            const isCompleted = !!s.completedAt;
            const isFailed = !!s.failedAt;
            const date = s.completedAt ?? s.failedAt ?? s.startedAt;
            return (
              <div
                key={s._id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 6,
                  padding: "3px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.1)"
                }}
              >
                <span style={{ marginTop: 1, flexShrink: 0 }}>
                  {isCompleted ? (
                    <CheckCircle style={{ fontSize: 14, color: "#4caf50" }} />
                  ) : isFailed ? (
                    <Error style={{ fontSize: 14, color: "#f44336" }} />
                  ) : (
                    <HourglassEmpty style={{ fontSize: 14, color: "#90caf9" }} />
                  )}
                </span>
                <span style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, lineHeight: "16px" }}>{s.title}</div>
                  {date && (
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 1 }}>
                      {moment(date).format("YYYY-MM-DD HH:mm")}
                    </div>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      }
      interactive
    >
      <div className={classes.stepsCell}>
        <Typography variant="body2" style={{ fontSize: 12 }}>
          {done}/{steps.length} — {current?.title ?? ""}
        </Typography>
      </div>
    </Tooltip>
  );
};

const ENTITY_TYPE_CONFIG: Record<string, { label: string; linkLabel?: string }> = {
  Booking: { label: "Bokning", linkLabel: "Öppna bokning" },
  BookingKit: { label: "Bokningskit", linkLabel: "Öppna bokningskit" },
  Consultant: { label: "Konsult", linkLabel: "Öppna konsult" },
  ConsultantContract: { label: "Konsultkontrakt" },
  Timereport: { label: "Tidrapport" },
  TimereportSendingToCustomer: { label: "Tidrapport till kund" }
};

const EntityLink = ({
  entityType,
  entityId,
  onOpen,
  className
}: {
  entityType: string;
  entityId: string;
  onOpen: (entityType: string, entityId: string) => void;
  className: string;
}) => {
  if (!entityId) return <Typography variant="body2">—</Typography>;

  const config = ENTITY_TYPE_CONFIG[entityType];
  const linkLabel = config?.linkLabel;

  if (linkLabel) {
    return (
      <span className={className} onClick={() => onOpen(entityType, entityId)}>
        {linkLabel}
      </span>
    );
  }

  return <Typography variant="body2">{config?.label ?? entityType}</Typography>;
};

export const WorkflowStatusPage = () => {
  const classes = useStyles();
  const globalStore = useGlobalStore();
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | "">("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const { data, loading } = useGetWorkflowRunsByOwnerQuery({
    variables: {
      status: statusFilter || undefined,
      workflowType: typeFilter || undefined,
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 10000
  });

  const runs = data?.getWorkflowRunsByOwner?.items ?? [];
  const total = data?.getWorkflowRunsByOwner?.total ?? 0;

  const handleFilterChange =
    (setter: (v: any) => void) => (e: React.ChangeEvent<{ value: unknown }>) => {
      setter(e.target.value);
      setPage(0);
    };

  const workflowTypeOptions = [
    { value: "", label: "Alla typer" },
    ...Object.entries(WORKFLOW_TYPE_LABELS).map(([value, label]) => ({ value, label }))
  ];

  const openEntity = (entityType: string, entityId: string) => {
    if (entityType === "Booking") {
      globalStore.navigation.open(
        BOOKING_SLIDER + Math.random(),
        new BookingProfileLocation(entityId, "finance")
      );
    } else if (entityType === "BookingKit") {
      globalStore.navigation.open(
        KIT_SLIDER + Math.random(),
        new KitProfileLocation(entityId)
      );
    } else if (entityType === "Consultant") {
      globalStore.navigation.open(
        CONSULTANT_SLIDER + Math.random(),
        new ConsultantProfileLocation(entityId)
      );
    }
  };

  const formatDate = (date?: Date | null) => {
    if (!date) return "—";
    return moment(date).format("YYYY-MM-DD HH:mm");
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">Workflow-status</Typography>
        <div className={classes.filters}>
          <Select
            className={classes.filterSelect}
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
            displayEmpty
            variant="outlined"
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <Select
            className={classes.filterSelect}
            value={typeFilter}
            onChange={handleFilterChange(setTypeFilter)}
            displayEmpty
            variant="outlined"
          >
            {workflowTypeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      {loading && !runs.length ? (
        <Box display="flex" justifyContent="center" pt={8}>
          <CircularProgress />
        </Box>
      ) : total === 0 ? (
        <div className={classes.emptyState}>
          <HourglassEmpty style={{ fontSize: 48, opacity: 0.3 }} />
          <Typography variant="body1" style={{ marginTop: 8 }}>
            Inga workflows hittades
          </Typography>
        </div>
      ) : (
        <Paper className={classes.tableContainer}>
          <TableContainer style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Typ</TableCell>
                  <TableCell>Kopplad entitet</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Steg</TableCell>
                  <TableCell>Schemalagd</TableCell>
                  <TableCell>Startad</TableCell>
                  <TableCell>Avslutad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run._id} hover>
                    <TableCell>
                      <div>{WORKFLOW_TYPE_LABELS[run.workflowType] ?? run.workflowType}</div>
                      {run.metadata?.label && (
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                          {run.metadata.label}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <EntityLink
                        entityType={run.entityType}
                        entityId={run.entityId}
                        onOpen={openEntity}
                        className={classes.entityLink}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={run.status} />
                      {run.errorMessage && (
                        <Typography className={classes.errorText}>
                          {run.errorMessage}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StepsCell steps={run.steps} />
                    </TableCell>
                    <TableCell>{formatDate(run.scheduledAt)}</TableCell>
                    <TableCell>{formatDate(run.startedAt)}</TableCell>
                    <TableCell>{formatDate(run.completedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_, newPage) => setPage(newPage)}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} av ${count}`}
          />
        </Paper>
      )}
    </div>
  );
};
